import React from 'react';
import { motion } from 'framer-motion';
import { Shield, ChevronRight } from 'lucide-react';

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using XceedEdge's platform, website, or any of our services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services. These terms apply to all visitors, students, instructors, and others who access or use our platform.`,
  },
  {
    title: '2. Use of the Platform',
    content: `You agree to use XceedEdge only for lawful purposes and in a manner consistent with all applicable local, national, and international laws. You must not misuse our platform by knowingly introducing viruses or other malicious code, attempting to gain unauthorized access to any part of the service, or engaging in any conduct that restricts others from using the platform.`,
  },
  {
    title: '3. Enrollment & Payments',
    content: `Course enrollments are subject to availability. All fees are quoted in Indian Rupees (INR) unless stated otherwise. Payments are processed securely through our payment partners. By enrolling in a course, you agree to pay the stated fees. We reserve the right to change pricing at any time, but existing enrollments will not be affected.`,
  },
  {
    title: '4. Refund Policy',
    content: `We offer a 7-day money-back guarantee on all courses. If you are not satisfied within the first 7 days of enrollment, you may request a full refund by contacting our support team. Refunds will be processed within 5–7 business days. No refunds are issued after the 7-day window or if a significant portion of the course content has been consumed.`,
  },
  {
    title: '5. Intellectual Property',
    content: `All content on the XceedEdge platform, including but not limited to text, graphics, logos, course materials, videos, and software, is the property of XceedEdge or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our explicit written consent.`,
  },
  {
    title: '6. User Accounts',
    content: `You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account. XceedEdge reserves the right to terminate accounts that violate these terms or engage in fraudulent activity.`,
  },
  {
    title: '7. Certificate of Completion',
    content: `Certificates issued by XceedEdge are recognition of course completion and do not constitute official academic qualifications unless explicitly stated. Certificates are non-transferable and tied to the individual learner's account. We reserve the right to revoke certificates in cases of academic dishonesty or violation of these terms.`,
  },
  {
    title: '8. Privacy Policy',
    content: `Your use of our platform is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our practices. By using XceedEdge, you consent to the collection and use of your information as described therein.`,
  },
  {
    title: '9. Disclaimer of Warranties',
    content: `XceedEdge provides its services on an "as is" and "as available" basis without warranties of any kind, either express or implied. We do not guarantee that our platform will be uninterrupted, error-free, or completely secure. While we strive to provide accurate and up-to-date information, we make no representations about the completeness or accuracy of the content.`,
  },
  {
    title: '10. Limitation of Liability',
    content: `To the fullest extent permitted by law, XceedEdge shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, our services. Our total liability to you for any claims arising under these terms shall not exceed the amount you paid to us in the 12 months preceding the claim.`,
  },
  {
    title: '11. Changes to Terms',
    content: `We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to the platform. Your continued use of XceedEdge after any changes constitutes your acceptance of the new terms. We encourage you to review these terms periodically.`,
  },
  {
    title: '12. Governing Law',
    content: `These Terms and Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising under or in connection with these terms shall be subject to the exclusive jurisdiction of the courts located in Nalgonda, Telangana, India.`,
  },
];

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero — light gradient + pt-24 so the fixed transparent navbar stays
          readable over it, matching the About / Careers / Contact heroes. A dark
          hero here hid the nav links, which use dark text until you scroll. */}
      <div className="relative overflow-hidden pt-24 pb-16 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="absolute top-0 left-1/3 w-96 h-64 rounded-full opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 70%)' }} />
        <div className="max-w-4xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-4 mb-6"
          >
            {/* 
            <span className="text-primary font-semibold text-sm tracking-widest uppercase font-body">Legal</span> */}
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading flex text-4xl sm:text-5xl font-bold text-foreground mb-4"
          >
          {/* <div className="w-12 me-3 h-12 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>  */}Terms & Conditions 
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-muted-foreground text-lg font-body"
          >
            Last updated: April 2026 &nbsp;·&nbsp; Please read these terms carefully before using our platform.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        {/* Quick Nav */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="glass rounded-2xl p-6 mb-12 border border-border/60"
        >
          <p className="font-heading font-bold text-sm uppercase tracking-widest text-muted-foreground mb-4">Table of Contents</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((s, i) => (
              <a
                key={i}
                href={`#section-${i}`}
                className="flex items-center gap-2 text-sm text-foreground/70 hover:text-primary transition-colors py-1 group"
              >
                <ChevronRight className="w-3.5 h-3.5 text-primary/50 group-hover:text-primary transition-colors" />
                {s.title}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Sections */}
        <div className="space-y-10">
          {sections.map((s, i) => (
            <motion.div
              key={i}
              id={`section-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="border-l-4 border-primary/30 pl-6 hover:border-primary transition-colors duration-300"
            >
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">{s.title}</h2>
              <p className="text-muted-foreground leading-relaxed font-body text-[15px]">{s.content}</p>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 glass-yellow rounded-2xl p-8 text-center"
        >
          <p className="font-heading text-xl font-bold text-foreground mb-2">Have Questions?</p>
          <p className="text-muted-foreground font-body mb-4">If you have any questions about these terms, please reach out to us.</p>
          <a href="mailto:info@xceededge.com" className="text-primary font-semibold hover:underline font-body">info@xceededge.com</a>
        </motion.div>
      </div>
    </div>
  );
}